import { first, last } from 'lodash';

import { isBookableTransportIngredient } from '@apps/main/modules/recipe/ingredients';
import type { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';

import type { AppSvgIconType } from '@libs/ui';
import { assertValue } from '@libs/utils';

const { t } = i18n.global;

export interface TripSummaryHeader {
  text?: string;
  icon?: AppSvgIconType;
}

export function getHeaderSummarySegments(
  recipeStore: ReturnType<typeof useRecipeStore>
): TripSummaryHeader[] {
  const summaryHeaderSegments: TripSummaryHeader[] = [];

  const firstIngredient = first(recipeStore.recipe);
  const lastIngredient = last(recipeStore.recipe);

  assertValue(
    firstIngredient,
    'TripOverviewHeaderMobile.vue > tripSummaryHeaderSegments > firstIngredient'
  );
  assertValue(
    lastIngredient,
    'TripOverviewHeaderMobile.vue > tripSummaryHeaderSegments > lastIngredient'
  );

  const isFirstOrLastIngredientTransports =
    isBookableTransportIngredient(firstIngredient) ||
    isBookableTransportIngredient(lastIngredient);

  const isFirstAndLastIngredientTransports =
    isBookableTransportIngredient(firstIngredient) &&
    isBookableTransportIngredient(lastIngredient);
  if (isFirstOrLastIngredientTransports) {
    if (
      isFirstAndLastIngredientTransports &&
      firstIngredient.fromPort === lastIngredient.toPort
    ) {
      summaryHeaderSegments.push({ text: firstIngredient.fromPortText });
      summaryHeaderSegments.push({ icon: 'change' });
      summaryHeaderSegments.push(
        getDestinationSegment(recipeStore.hotelOnlyRecipe, false)
      );
    } else {
      if (isBookableTransportIngredient(firstIngredient)) {
        summaryHeaderSegments.push({ text: firstIngredient.fromPortText });
        summaryHeaderSegments.push({ icon: 'arrowRight' });
      }
      summaryHeaderSegments.push(
        getDestinationSegment(recipeStore.hotelOnlyRecipe, true)
      );

      if (isBookableTransportIngredient(lastIngredient)) {
        summaryHeaderSegments.push({ icon: 'arrowRight' });
        summaryHeaderSegments.push({ text: lastIngredient.toPortText });
      }
    }
  } else {
    summaryHeaderSegments.push(
      getDestinationSegment(recipeStore.hotelOnlyRecipe, false)
    );
  }
  return summaryHeaderSegments;
}

function getDestinationSegment(
  hotelOnlyRecipe: HotelIngredient[],
  useIcon: boolean
): TripSummaryHeader {
  const numberOfDestinations = hotelOnlyRecipe.length;

  if (numberOfDestinations > 1) {
    return {
      text: `${numberOfDestinations}${
        useIcon
          ? ''
          : ` ${t(
              'planner.destination',
              numberOfDestinations
            ).toLocaleLowerCase()}`
      }`,
      icon: useIcon ? 'locationMark' : undefined,
    };
  } else {
    const city = hotelOnlyRecipe[0]?.displayText.split(',')[0];
    return {
      text: city,
    };
  }
}
